// Админская модерация отзывов.
// ───────────────────────────────
// Три таба: pending (по умолчанию, очередь) / approved / rejected.
// Очередь pending отсортирована по createdAt ASC (старые первыми — fair queue).
// Approve/Reject — inline-кнопки на карточке. Reject требует причину.
//
// avgRating на Course пересчитывается на бэке при approve/reject — фронт
// просто рендерит и инвалидирует кэши через RTK Query tags.

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  Star,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  MessageSquare,
  ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useGetAdminReviewsQuery,
  useModerateReviewMutation,
} from '../../api/apiSlice';

const STATUS_TABS = [
  { id: 'pending', label: 'В очереди', icon: Clock, tone: 'amber' },
  { id: 'approved', label: 'Одобренные', icon: CheckCircle, tone: 'emerald' },
  { id: 'rejected', label: 'Отклонённые', icon: XCircle, tone: 'red' },
];

const StarRow = ({ value }) => (
  <div className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        className={`w-4 h-4 ${
          s <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'
        }`}
      />
    ))}
  </div>
);

const AdminReviewsPage = () => {
  const [status, setStatus] = useState('pending');
  const [page, setPage] = useState(1);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const { data, isLoading, isFetching } = useGetAdminReviewsQuery({
    status,
    page,
    limit: 20,
  });
  const [moderate, { isLoading: moderating }] = useModerateReviewMutation();

  const items = data?.items || [];
  const counts = data?.counts || { pending: 0, approved: 0, rejected: 0 };

  const handleApprove = async (reviewId) => {
    try {
      await moderate({ reviewId, status: 'approved' }).unwrap();
      toast.success('Отзыв одобрен');
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось одобрить');
    }
  };

  const handleReject = async (reviewId) => {
    try {
      await moderate({
        reviewId,
        status: 'rejected',
        rejectionReason: rejectionReason.trim() || undefined,
      }).unwrap();
      toast.success('Отзыв отклонён');
      setRejectingId(null);
      setRejectionReason('');
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось отклонить');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <MessageSquare className="w-7 h-7 text-indigo-600" />
          Модерация отзывов
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Одобренные отзывы автоматически попадают на страницу курса и в агрегат рейтинга.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {STATUS_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = status === tab.id;
          const count = counts[tab.id] || 0;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setStatus(tab.id);
                setPage(1);
              }}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                active
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${
                  active
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-12 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 dark:bg-gray-800">
            <CheckCircle className="w-7 h-7 text-emerald-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Очередь пуста
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {status === 'pending'
              ? 'Все отзывы обработаны.'
              : `Нет отзывов со статусом «${STATUS_TABS.find((t) => t.id === status)?.label}».`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((review) => (
            <div
              key={review._id}
              className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm"
            >
              <div className="p-5 sm:p-6">
                {/* Header: course + author */}
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  {/* R2 cost-save: НЕ грузим coverImageUrl в админ-списке.
                      Каждое открытие = N R2-reads. Буквенный thumbnail вместо. */}
                  <Link
                    to={`/course/${review.courseId?._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 group min-w-0"
                  >
                    <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 text-sm font-semibold shrink-0">
                      {(review.courseId?.title || '?').trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                        {review.courseId?.title || 'Курс'}
                        <ExternalLink className="inline w-3.5 h-3.5 ml-1.5 opacity-50" />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Курс
                      </div>
                    </div>
                  </Link>

                  <div className="flex items-center gap-3 min-w-0">
                    {/* Не грузим аватарки через R2 в admin-списке отзывов —
                        каждый ряд = Cloudflare GET = деньги/квота. Initials-кружок
                        достаточен для модерации (имя+email видны рядом). */}
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold shrink-0">
                      {(review.userId?.name || review.userId?.email || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 dark:text-white truncate">
                        {review.userId?.name || 'Без имени'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {review.userId?.email}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rating + date */}
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <StarRow value={review.rating} />
                  <span className="text-sm font-bold text-amber-600 tabular-nums">
                    {review.rating} / 5
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(review.createdAt).toLocaleString('ru-RU', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>

                {/* Text */}
                <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-line break-words [overflow-wrap:anywhere] mb-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
                  {review.text}
                </p>

                {/* Existing rejection reason if rejected */}
                {review.status === 'rejected' && review.rejectionReason && (
                  <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/40 p-3 text-sm text-red-700 dark:text-red-300">
                    <span className="font-semibold inline-flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4" /> Причина отказа:
                    </span>{' '}
                    {review.rejectionReason}
                  </div>
                )}

                {/* Rejection form (inline) */}
                {rejectingId === review._id && (
                  <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/40 p-3">
                    <label className="text-xs font-semibold text-red-700 dark:text-red-300 mb-1.5 block">
                      Причина отказа (опционально, увидит автор)
                    </label>
                    <input
                      type="text"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Например: реклама / оскорбления / спам"
                      maxLength={500}
                      className="w-full rounded-lg border border-red-200 dark:border-red-900/40 bg-white dark:bg-gray-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2">
                  {review.status !== 'approved' && (
                    <button
                      type="button"
                      onClick={() => handleApprove(review._id)}
                      disabled={moderating}
                      className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 transition"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Одобрить
                    </button>
                  )}
                  {review.status !== 'rejected' && (
                    <>
                      {rejectingId === review._id ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleReject(review._id)}
                            disabled={moderating}
                            className="inline-flex items-center gap-2 rounded-xl bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 transition"
                          >
                            <XCircle className="w-4 h-4" />
                            Подтвердить отказ
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setRejectingId(null);
                              setRejectionReason('');
                            }}
                            className="rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            Отмена
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setRejectingId(review._id);
                            setRejectionReason(review.rejectionReason || '');
                          }}
                          className="inline-flex items-center gap-2 rounded-xl border border-red-300 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-950/20 text-red-600 dark:text-red-400 px-4 py-2 text-sm font-semibold transition"
                        >
                          <XCircle className="w-4 h-4" />
                          Отклонить
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {data && data.pageCount > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || isFetching}
                className="rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                ←
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-300 px-3">
                Стр. {data.page} из {data.pageCount}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(data.pageCount, p + 1))}
                disabled={page >= data.pageCount || isFetching}
                className="rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminReviewsPage;
